import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { alpha } from "@mui/material/styles";
import { LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import moment from "moment";
import Loader from "@jumbo/components/Loader";
import { getMeeshoPayoutBreakdown } from "app/redux/slice/meeshoOrderManagementSlice";

const fmt = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const fmtPrice = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const monthLabel = (month) => moment(month, "YYYY-MM").format("MMM'YY");

const COSTING_TITLE = {
  delivered: "Delivered",
  exchanged: "Exchanged",
  claims: "Claims Accepted",
};

const COSTING_COLUMNS = [
  { key: "product", label: "Product", align: "left" },
  { key: "pieces", label: "Pieces", align: "right" },
  { key: "unitPrice", label: "Unit Price", align: "right" },
  { key: "cost", label: "Cost", align: "right" },
];
const DEFAULT_COSTING_SORT = { by: "pieces", dir: "desc" };

// Sorted copy; a missing unit price ("Not set") always goes last
const sortCosting = (items = [], { by, dir }) => {
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = a[by];
    const y = b[by];
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    const diff = by === "product" ? String(x).localeCompare(String(y)) : x - y;
    return diff * sign || String(a.product).localeCompare(String(b.product));
  });
};

// Product cost of the orders behind one row, grouped by product
const CostingDialog = ({ type, month, costing, orders, onClose }) => {
  const [sort, setSort] = useState(DEFAULT_COSTING_SORT);

  // Every opening starts from the default order
  useEffect(() => {
    if (type) setSort(DEFAULT_COSTING_SORT);
  }, [type]);

  const toggleSort = (by) =>
    setSort((s) =>
      s.by === by
        ? { by, dir: s.dir === "asc" ? "desc" : "asc" }
        : { by, dir: by === "product" ? "asc" : "desc" }
    );

  return (
    <Dialog open={Boolean(type)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {COSTING_TITLE[type]} – Product Costing (
        {moment(month, "YYYY-MM").format("MMM YYYY")})
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {orders} orders · {costing?.totalPieces} pieces
          {costing?.totalPieces !== orders &&
            " (some orders have more than one piece; cost is per piece)"}
        </Typography>
        {costing?.missingCost > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {costing.missingCost} SKU(s) are not linked to any product in the
            product master; they are counted as ₹0.
          </Alert>
        )}
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COSTING_COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{ fontWeight: 700 }}
                  sortDirection={sort.by === col.key ? sort.dir : false}
                >
                  <TableSortLabel
                    active={sort.by === col.key}
                    direction={sort.by === col.key ? sort.dir : "desc"}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortCosting(costing?.items, sort).map((i) => (
              <TableRow key={i.skus.join("|")} hover>
                <TableCell>
                  {i.product || "-"}
                  {!i.inMaster && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      display="block"
                    >
                      Not in product master · SKU {i.skus.join(", ")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{i.pieces}</TableCell>
                <TableCell align="right">
                  {i.unitPrice === null ? (
                    <Typography variant="body2" color="warning.main">
                      Not set
                    </Typography>
                  ) : (
                    <>
                      {fmtPrice(i.unitPrice)}
                      {i.priceVaries && (
                        <Typography variant="caption" color="text.secondary">
                          {" "}
                          avg
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell align="right">{fmtPrice(i.cost)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Final Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {costing?.totalPieces}
              </TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmtPrice(costing?.totalCost)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ENTRY_TYPE = {
  paid: { label: "Paid", color: "success" },
  processing: { label: "Paid · no transaction ID", color: "info" },
  upcoming: { label: "Upcoming", color: "warning" },
  unscheduled: { label: "Unscheduled", color: "default" },
  ads: { label: "Ads deducted", color: "error" },
  noPayment: { label: "No payment", color: "default" },
};
const ENTRY_COLUMNS = [
  { key: "date", label: "Payment Date", align: "left" },
  { key: "type", label: "Type", align: "left" },
  { key: "transactionId", label: "Transaction ID", align: "left" },
  { key: "orders", label: "Orders", align: "right" },
  { key: "statuses", label: "Order Status", align: "left", sortable: false },
  { key: "amount", label: "Amount", align: "right" },
];
const DEFAULT_ENTRY_SORT = { by: "date", dir: "asc" };

// The rows of the breakdown each payment amount is split into
const PAYOUT_PARTS = [
  { key: "orders", label: "Orders" },
  { key: "commission", label: "Commission" },
  { key: "tax", label: "Tax Component" },
  { key: "returnShipping", label: "Return Shipping Fee" },
  { key: "exchangeShipping", label: "Exchange Shipping Fee" },
  { key: "claims", label: "Claims" },
  { key: "ads", label: "Ads Cost + Ads GST" },
  { key: "additionalServices", label: "Additional Services" },
];
const entryKey = (e) => `${e.type}|${e.date}|${e.transactionId}`;
const amountSx = (v) => ({ color: v < 0 ? "error.main" : "inherit" });

// One payment's amount by breakdown row (rows it has nothing in are left out)
const PartsLine = ({ parts = {}, amount }) => {
  const used = PAYOUT_PARTS.filter((p) => Math.abs(parts[p.key] || 0) >= 0.005);
  return (
    <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 3, rowGap: 0.5 }}>
      {used.map((p) => (
        <Typography key={p.key} variant="caption" color="text.secondary">
          {p.label}:{" "}
          <Box
            component="span"
            sx={{ fontWeight: 600, ...amountSx(parts[p.key]) }}
          >
            {fmtPrice(parts[p.key])}
          </Box>
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        ={" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {fmtPrice(amount)}
        </Box>
      </Typography>
    </Stack>
  );
};

// What the orders of a payment are, one status per line, labelled as in the
// Orders rows of the breakdown. Orders already counted in an earlier payment
// (return deduction, fee, claim) are shown apart, so the totals match.
const statusSummary = (statuses = [], repeatOrders = 0) =>
  statuses.length || repeatOrders ? (
    <>
      {statuses.map((s) => (
        <Box component="span" key={s.status} sx={{ display: "block" }}>
          {s.orders} {s.status}
        </Box>
      ))}
      {repeatOrders > 0 && (
        <Box component="span" sx={{ display: "block", fontStyle: "italic" }}>
          +{repeatOrders} counted in an earlier payment
        </Box>
      )}
    </>
  ) : (
    "-"
  );

// Status → orders over all entries; equals the Orders rows of the breakdown
const statusTotals = (entries = []) => {
  const totals = new Map();
  for (const e of entries)
    for (const s of e.statuses)
      totals.set(s.status, (totals.get(s.status) || 0) + s.orders);
  return [...totals]
    .map(([status, orders]) => ({ status, orders }))
    .sort((a, b) => b.orders - a.orders);
};

// Sorted copy; an entry with no date (unscheduled) always goes last
const sortEntries = (entries = [], { by, dir }) => {
  const sign = dir === "asc" ? 1 : -1;
  return [...entries].sort((a, b) => {
    if (by === "date" && (!a.date || !b.date)) {
      return a.date === b.date ? 0 : a.date ? -1 : 1;
    }
    const x = a[by] ?? "";
    const y = b[by] ?? "";
    const diff =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y));
    return diff * sign || String(a.date).localeCompare(String(b.date));
  });
};

// The Final Payout entry by entry: which payment brought what, on which day
const PayoutEntriesDialog = ({ open, month, entries = [], total, onClose }) => {
  const [sort, setSort] = useState(DEFAULT_ENTRY_SORT);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (open) {
      setSort(DEFAULT_ENTRY_SORT);
      setExpanded(new Set());
    }
  }, [open]);

  const toggleExpanded = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Each payout row as the sum over all payments; equals the breakdown table
  const partTotals = Object.fromEntries(
    PAYOUT_PARTS.map((p) => [
      p.key,
      entries.reduce((s, e) => s + (e.parts?.[p.key] || 0), 0),
    ])
  );

  const toggleSort = (by) =>
    setSort((s) =>
      s.by === by
        ? { by, dir: s.dir === "asc" ? "desc" : "asc" }
        : { by, dir: ["orders", "amount"].includes(by) ? "desc" : "asc" }
    );

  const sumOf = (types) =>
    entries
      .filter((e) => types.includes(e.type))
      .reduce((s, e) => s + e.amount, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Final Payout – Payments ({moment(month, "YYYY-MM").format("MMM YYYY")})
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Money for orders placed in this month, by the payment it came in. Each
          order is counted once, in its first payment, under the same status as
          the Orders rows.
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`Paid ${fmtPrice(sumOf(["paid", "processing"]))}`}
          />
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Not paid yet ${fmtPrice(
              sumOf(["upcoming", "unscheduled"])
            )}`}
          />
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`Ads ${fmtPrice(sumOf(["ads"]))}`}
          />
        </Stack>
        <Typography variant="subtitle2" mb={0.5}>
          All payments by payout row
        </Typography>
        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {PAYOUT_PARTS.map((p) => (
                  <TableCell key={p.key} align="right" sx={{ fontWeight: 700 }}>
                    {p.label}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Final Payout
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {PAYOUT_PARTS.map((p) => (
                  <TableCell
                    key={p.key}
                    align="right"
                    sx={{
                      whiteSpace: "nowrap",
                      ...amountSx(partTotals[p.key]),
                    }}
                  >
                    {fmtPrice(partTotals[p.key])}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                >
                  {fmtPrice(total)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {ENTRY_COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{ fontWeight: 700 }}
                  sortDirection={sort.by === col.key ? sort.dir : false}
                >
                  {col.sortable === false ? (
                    col.label
                  ) : (
                    <TableSortLabel
                      active={sort.by === col.key}
                      direction={sort.by === col.key ? sort.dir : "asc"}
                      onClick={() => toggleSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortEntries(entries, sort).map((e) => {
              const key = entryKey(e);
              const isOpen = expanded.has(key);
              const hasParts = e.type !== "noPayment";
              return (
                <React.Fragment key={key}>
                  <TableRow
                    hover
                    onClick={hasParts ? () => toggleExpanded(key) : undefined}
                    sx={{
                      cursor: hasParts ? "pointer" : "default",
                      "& > td": isOpen ? { borderBottom: "none" } : {},
                    }}
                  >
                    <TableCell padding="checkbox">
                      {hasParts && (
                        <IconButton size="small" aria-label="Show split">
                          {isOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {e.date ? moment(e.date).format("DD/MM/YYYY") : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={ENTRY_TYPE[e.type]?.color}
                        label={ENTRY_TYPE[e.type]?.label ?? e.type}
                      />
                    </TableCell>
                    <TableCell>{e.transactionId || "-"}</TableCell>
                    <TableCell align="right">
                      {e.type === "ads" ? "-" : e.orders}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {statusSummary(e.statuses, e.repeatOrders)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: e.amount < 0 ? "error.main" : "inherit" }}
                    >
                      {fmtPrice(e.amount)}
                    </TableCell>
                  </TableRow>
                  {hasParts && (
                    <TableRow>
                      <TableCell
                        colSpan={ENTRY_COLUMNS.length + 1}
                        sx={{
                          py: 0,
                          ...(isOpen ? {} : { borderBottom: "none" }),
                        }}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ pl: 6, pb: 1.5 }}>
                            <PartsLine parts={e.parts} amount={e.amount} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            <TableRow>
              <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                Total (Final Payout)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {entries.reduce((s, e) => s + e.orders, 0)}
              </TableCell>
              <TableCell>
                <Typography variant="caption" fontWeight={700}>
                  {statusSummary(statusTotals(entries))}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmtPrice(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Compares magnitudes, like Seller Insights. `costRow` flips the colour:
// a bigger cost is bad (red), a bigger income is good (green).
const Change = ({ current, previous, previousMonth, costRow }) => {
  const theme = useTheme();
  const cur = Math.abs(Number(current || 0));
  const prev = Math.abs(Number(previous || 0));
  if (!prev || cur === prev) return <Typography variant="body2">-</Typography>;

  const up = cur > prev;
  const pct = ((cur - prev) / prev) * 100;
  const good = costRow ? !up : up;
  const color = good ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box>
      <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
        {up ? "▲" : "▼"} {pct.toFixed(2)}%
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {fmt(Math.abs(cur - prev))} {up ? "Higher" : "Lower"} than{" "}
        {monthLabel(previousMonth)}
      </Typography>
    </Box>
  );
};

const cellSx = { verticalAlign: "top", py: 1.5 };
const numSx = { ...cellSx, textAlign: "right", whiteSpace: "nowrap" };

const PayoutBreakdown = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [month, setMonth] = useState(
    moment().subtract(1, "month").startOf("month").toDate()
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [costingType, setCostingType] = useState(null);
  const [entriesOpen, setEntriesOpen] = useState(false);
  // View-only, never sent to the API; cleared when the month changes
  const [otherExpense, setOtherExpense] = useState("");

  const fetchData = useCallback(
    (selected) => {
      setLoading(true);
      dispatch(
        getMeeshoPayoutBreakdown({ month: moment(selected).format("YYYY-MM") })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) setData(res?.data?.data);
          else
            enqueueSnackbar("Failed to fetch payout breakdown", {
              variant: "error",
            });
        })
        .catch(() =>
          enqueueSnackbar("Failed to fetch payout breakdown", {
            variant: "error",
          })
        )
        .finally(() => setLoading(false));
    },
    [dispatch, enqueueSnackbar]
  );

  useEffect(() => {
    if (month && moment(month).isValid()) fetchData(month);
    setOtherExpense("");
  }, [month, fetchData]);

  const cur = data?.current;
  const prev = data?.previous;
  const PRODUCT_COST_ROWS = [
    { key: "delivered", label: "Delivered" },
    { key: "claims", label: "Claims" },
    { key: "exchanged", label: "Exchanged" },
  ];
  // Order count shown with a costing, the same number as in the rows above
  const costingOrders = (key) =>
    key === "claims"
      ? cur?.claims.count ?? 0
      : cur?.orders.buckets.find((b) => b.key === key)?.count ?? 0;
  const productCost = PRODUCT_COST_ROWS.reduce(
    (s, r) => s + (cur?.costing?.[r.key]?.totalCost || 0),
    0
  );
  const grossProfit = (cur?.finalPayout || 0) - productCost;
  const netProfit = grossProfit - (Number(otherExpense) || 0);
  const profitColor = (v) =>
    v < 0 ? theme.palette.error.main : theme.palette.success.main;
  const start = moment(data?.month, "YYYY-MM");
  const period = `${start.format("D MMM 'YY")} - ${start
    .clone()
    .endOf("month")
    .format("D MMM 'YY")}`;

  const change = (current, previous, costRow = false) => (
    <Change
      current={current}
      previous={previous}
      previousMonth={data?.previousMonth}
      costRow={costRow}
    />
  );

  const simpleRow = (type, details, amount, changeCell) => (
    <TableRow>
      <TableCell sx={cellSx}>{type}</TableCell>
      <TableCell sx={cellSx}>{details}</TableCell>
      <TableCell sx={numSx}>{fmt(amount)}</TableCell>
      <TableCell sx={{ ...numSx, fontWeight: 600 }}>{fmt(amount)}</TableCell>
      <TableCell sx={cellSx}>{changeCell}</TableCell>
    </TableRow>
  );

  // `costing` = key in cur.costing; makes the label open the costing modal
  const countDetails = (label, count, costing) => (
    <Stack direction="row" justifyContent="space-between">
      {costing && cur?.costing?.[costing] ? (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => setCostingType(costing)}
        >
          {label}
        </Link>
      ) : (
        <span>{label}</span>
      )}
      <span>{count}</span>
    </Stack>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={2}
      >
        <Typography variant="h4" component="h1">
          Payout Breakdown
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={["year", "month"]}
            openTo="month"
            label="Order Month"
            inputFormat="MMM yyyy"
            maxDate={new Date()}
            value={month}
            onChange={setMonth}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        </LocalizationProvider>
      </Stack>

      {loading ? (
        <Loader />
      ) : !cur ? (
        <Alert severity="info">No data available for the selected month.</Alert>
      ) : (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="baseline" spacing={1.5} mb={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Payout Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({period})
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Payment data till{" "}
              {data.paymentDataTill
                ? moment.utc(data.paymentDataTill).format("DD MMM YYYY")
                : "-"}
              {" · "}Ads as deducted by Meesho (payment file), till{" "}
              {data.adsDataTill
                ? moment.utc(data.adsDataTill).format("DD MMM YYYY")
                : "-"}
              {data.outstandingIncluded &&
                ` · Outstanding (not yet paid) as of ${moment(
                  data.outstandingDataAsOf
                ).format("DD MMM YYYY, hh:mm A")}`}
            </Typography>
            {data.outstandingDataAsOf && !data.outstandingIncluded && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The outstanding payment file is older than the latest payment
                file, so it is not counted. Import a fresh outstanding file.
              </Alert>
            )}

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>
                      Value
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>
                      Payout
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>What Changed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={cellSx}>Orders</TableCell>
                    <TableCell sx={cellSx}>
                      {cur.orders.buckets.map((b) => (
                        <Box key={b.key} sx={{ py: 0.5 }}>
                          {countDetails(b.label, b.count)}
                        </Box>
                      ))}
                      <Box
                        sx={{
                          borderTop: `1px dashed ${theme.palette.divider}`,
                          mt: 0.5,
                          pt: 1,
                        }}
                      >
                        {countDetails("Total Orders", cur.orders.totalCount)}
                      </Box>
                    </TableCell>
                    <TableCell sx={numSx}>
                      {cur.orders.buckets.map((b) => (
                        <Box key={b.key} sx={{ py: 0.5 }}>
                          {fmt(b.value)}
                        </Box>
                      ))}
                      <Box
                        sx={{
                          borderTop: `1px dashed ${theme.palette.divider}`,
                          mt: 0.5,
                          pt: 1,
                        }}
                      >
                        {fmt(cur.orders.totalValue)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 600 }}>
                      {fmt(cur.orders.totalValue)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {change(cur.orders.totalValue, prev?.orders.totalValue)}
                    </TableCell>
                  </TableRow>

                  {simpleRow(
                    "Commission",
                    "-",
                    cur.commission,
                    change(cur.commission, prev?.commission, true)
                  )}
                  {simpleRow(
                    "Tax Component",
                    "-",
                    cur.tax,
                    change(cur.tax, prev?.tax, true)
                  )}
                  {simpleRow(
                    "Return Shipping Fee",
                    countDetails("Returned Orders", cur.returnShipping.count),
                    cur.returnShipping.amount,
                    change(
                      cur.returnShipping.amount,
                      prev?.returnShipping.amount,
                      true
                    )
                  )}
                  {simpleRow(
                    "Exchange Shipping Fee",
                    countDetails(
                      "Exchanged Orders",
                      cur.exchangeShipping.count
                    ),
                    cur.exchangeShipping.amount,
                    change(
                      cur.exchangeShipping.amount,
                      prev?.exchangeShipping.amount,
                      true
                    )
                  )}
                  {simpleRow(
                    "Claims Details",
                    countDetails("Claims Accepted", cur.claims.count),
                    cur.claims.amount,
                    change(cur.claims.amount, prev?.claims.amount)
                  )}
                  {simpleRow(
                    "Ads Cost + Ads GST",
                    "-",
                    cur.ads,
                    change(cur.ads, prev?.ads, true)
                  )}

                  <TableRow>
                    <TableCell sx={cellSx}>Additional Services</TableCell>
                    <TableCell sx={cellSx}>
                      {cur.additionalServices.items.length
                        ? cur.additionalServices.items.map((i) => (
                            <Box key={i.label} sx={{ py: 0.5 }}>
                              {i.label}
                            </Box>
                          ))
                        : "-"}
                    </TableCell>
                    <TableCell sx={numSx}>
                      {cur.additionalServices.items.map((i) => (
                        <Box key={i.label} sx={{ py: 0.5 }}>
                          {fmt(i.amount)}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 600 }}>
                      {fmt(cur.additionalServices.total)}
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        sx={{ fontWeight: 700 }}
                        onClick={() => setEntriesOpen(true)}
                      >
                        Final Payout
                      </Link>
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                    <TableCell sx={numSx}>-</TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 700 }}>
                      {fmt(cur.finalPayout)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {change(cur.finalPayout, prev?.finalPayout)}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={cellSx}>Product Cost</TableCell>
                    <TableCell sx={cellSx}>
                      {PRODUCT_COST_ROWS.map((r) => (
                        <Box key={r.key} sx={{ py: 0.5 }}>
                          {countDetails(r.label, costingOrders(r.key), r.key)}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell sx={numSx}>
                      {PRODUCT_COST_ROWS.map((r) => (
                        <Box key={r.key} sx={{ py: 0.5 }}>
                          {fmt(-(cur.costing?.[r.key]?.totalCost || 0))}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 600 }}>
                      {fmt(-productCost)}
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                  </TableRow>

                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                  >
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                      Gross Profit/Loss
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" color="text.secondary">
                        Final Payout − Product Cost
                      </Typography>
                    </TableCell>
                    <TableCell sx={numSx}>-</TableCell>
                    <TableCell
                      sx={{
                        ...numSx,
                        fontWeight: 700,
                        color: profitColor(grossProfit),
                      }}
                    >
                      {fmt(grossProfit)}
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ ...cellSx, verticalAlign: "middle" }}>
                      Other Expense
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                    <TableCell sx={numSx}>-</TableCell>
                    <TableCell sx={numSx}>
                      <TextField
                        type="number"
                        size="small"
                        placeholder="0"
                        value={otherExpense}
                        onChange={(e) => setOtherExpense(e.target.value)}
                        inputProps={{
                          min: 0,
                          step: "any",
                          style: { textAlign: "right" },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                        sx={{ width: 170 }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                  </TableRow>

                  <TableRow
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                  >
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                      NET PROFIT
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" color="text.secondary">
                        Gross Profit/Loss − Other Expense
                      </Typography>
                    </TableCell>
                    <TableCell sx={numSx}>-</TableCell>
                    <TableCell
                      sx={{
                        ...numSx,
                        fontWeight: 700,
                        color: profitColor(netProfit),
                      }}
                    >
                      {fmt(netProfit)}
                    </TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {cur.orders.buckets.some(
              (b) => b.key === "paymentPending" && b.count > 0
            ) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                "Payment Pending" orders are delivered but have no row in any
                payment or outstanding file yet, so whether they will be
                returned or exchanged is not known. These numbers will change
                after the next payment / outstanding file import.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <CostingDialog
        type={costingType}
        month={data?.month}
        costing={cur?.costing?.[costingType]}
        orders={costingOrders(costingType)}
        onClose={() => setCostingType(null)}
      />

      <PayoutEntriesDialog
        open={entriesOpen && Boolean(cur)}
        month={data?.month}
        entries={cur?.payoutEntries}
        total={cur?.finalPayout}
        onClose={() => setEntriesOpen(false)}
      />
    </Box>
  );
};

export default PayoutBreakdown;
